// powerCapabilitySeries.ts
import Highcharts from 'highcharts';

import type { TimeseriesMetric } from '@/pcs/domain/entities/pcs-timeseries';

import { baseColors } from '../../color-palette';
import { generateShade } from '../../color-utils';

export const generatePowerCapabilitySeries = (
  powerCapability: TimeseriesMetric,
): {
  series: Highcharts.SeriesOptionsType[];
  plotLines: Highcharts.YAxisPlotLinesOptions[];
} => {
  // color map
  const uniqueIds = Array.from(
    new Set(powerCapability.lines.map((line) => line.id)),
  );
  const idColorMap: Record<string, string> = {};
  uniqueIds.forEach((id, index) => {
    idColorMap[id] = baseColors[index % baseColors.length];
  });

  const series: Highcharts.SeriesOptionsType[] = [];
  const plotLines: Highcharts.YAxisPlotLinesOptions[] = [];

  // Filter out lines that are always 100% OK
  const filteredLines = powerCapability.lines.filter((line) => {
    const okSubLine = line.subLines.find((s) => s.label === 'ok');
    if (!okSubLine) return true;
    return !okSubLine.data.every((point) => point.value === 100);
  });

  const opacity = 0.2 / uniqueIds.length;

  filteredLines.forEach((line) => {
    // Base color set
    const darkerColor = generateShade(idColorMap[line.id], 'gap');
    const lighterColor = generateShade(idColorMap[line.id], 'invalid');

    // We'll handle charge (cha) & discharge (dch) lines
    ['cha', 'dch'].forEach((prefix) => {
      const avgSubLine = line.subLines.find((s) => s.label === `${prefix}_avg`);
      const minSubLine = line.subLines.find((s) => s.label === `${prefix}_min`);
      const maxSubLine = line.subLines.find((s) => s.label === `${prefix}_max`);

      const signFactor = prefix === 'dch' ? -1 : 1;

      // 1) Range — fall back through avg when either bound is null
      if (minSubLine && maxSubLine) {
        series.push({
          type: 'arearange',
          name: `${line.fullFrontendID} ${prefix} range (${line.idStatus})`,
          data: minSubLine.data.map((point, idx) => {
            const ts = point.timestamp.getTime();
            const minVal = point.value;
            const maxVal = maxSubLine.data[idx].value;
            const avgVal = avgSubLine?.data[idx]?.value ?? null;
            let lower = minVal ?? avgVal ?? maxVal;
            let upper = maxVal ?? avgVal ?? minVal;
            // Guard against all-null scenario
            if (lower == null || upper == null) return [ts, null, null];
            // Ensure valid range (defensive against anomalous data)
            if (lower > upper) [lower, upper] = [upper, lower];
            return [ts, signFactor * lower, signFactor * upper];
          }),
          color: lighterColor,
          lineWidth: 0,
          fillOpacity: opacity,
          showInLegend: false,
          marker: {
            enabled: false,
          },
          zIndex: 1,
        });
      }

      // 2) Avg line — fill gaps with min/max fallback for continuity
      if (avgSubLine) {
        series.push({
          type: 'line',
          name: `${line.fullFrontendID} ${prefix} avg (${line.idStatus})`,
          data: avgSubLine.data.map((p, idx) => {
            const val =
              p.value ??
              minSubLine?.data[idx]?.value ??
              maxSubLine?.data[idx]?.value;
            return [
              p.timestamp.getTime(),
              val != null ? signFactor * val : null,
            ];
          }),
          zIndex: 2,
          color: darkerColor,
          showInLegend: false,
          marker: {
            enabled: true,
            symbol: 'circle',
            radius: 2,
          },
        });
      }

      // 3) Dedicated min line for single-PCS view (avoids fleet clutter)
      if (uniqueIds.length === 1 && minSubLine) {
        series.push({
          type: 'line',
          name: `${line.fullFrontendID} ${prefix} min`,
          data: minSubLine.data.map((p) => [
            p.timestamp.getTime(),
            p.value != null ? signFactor * p.value : null,
          ]),
          color: darkerColor,
          dashStyle: 'ShortDash',
          lineWidth: 1,
          showInLegend: false,
          zIndex: 1,
          marker: { enabled: false },
        });
      }
    });
  });

  // Additional references can be turned into plotLines
  powerCapability.references?.forEach((ref) => {
    const isDischarge = ref.name.startsWith('dch_');
    const signFactor = isDischarge ? -1 : 1;
    plotLines.push({
      value: signFactor * ref.value,
      color: '#28a745',
      dashStyle: 'Dash',
      width: 2,
      zIndex: 5,
      label: {
        text: ref.label,
        align: 'right',
        style: { color: '#28a745' },
      },
    });
  });

  return { series, plotLines };
};
